<template>
  <b-container>
    <b-card class="mb-4">
      <b-row align-v="center">
        <b-col cols="12" md="6" lg="4">
          <label class="mb-2 font-weight-bold">Select Date Range</label>
          <DatePicker
            v-model="dateRange"
            range
            format="YYYY-MM-DD"
            placeholder="Select date range"
            @change="handleDateRangeChange"
            size="sm"
            style="width: 100%"
          />
        </b-col>
        <b-col cols="12" md="6" lg="4" class="mt-3 mt-md-0">
          <b-button
            variant="primary"
            :disabled="!dateRange"
            @click="handleDateRangeChange"
            class="ml-md-2"
          >
            Refresh Reports
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <div class="d-flex justify-content-center mb-1">
      <b-spinner
        variant="success"
        label="Spinning"
        block
        v-if="loading || isLocalLoading"
        style="width: 3rem; height: 3rem"
      ></b-spinner>
    </div>
    
    <b-alert show variant="info" v-if="!dateRange && !loading && !isLocalLoading">
      Please select a date range above to view the reports.
    </b-alert>

    <b-row v-if="dateRange && !loading && !isLocalLoading">
      <b-col cols="12">
        <InventoryValueperProduct :data="inventoryValueAnalysisData"/>
      </b-col>
      <b-col cols="12">
        <QuantityChart 
          :data="inventoryQtyAnalysisData" 
          :productList="productList"
          :selectedProductId="firstProductId" 
        />
      </b-col>
      <!-- Third chart commented out as requested -->
      <!-- <b-col cols="12">
        <Lead :data="transactionCounts" />
      </b-col> -->
      <b-col cols="12">
        <BarChart :data="warehouseDistributionData" />
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import InventoryValueperProduct from "./InventoryValueperProduct.vue";
import Lead from "./Lead.vue";
import QuantityChart from "./QuantityChart.vue";
import BarChart from "./BarChart.vue";
import ChartjsBarChart from "./ChartjsBarChart.vue";
import InventoryChart from "./InventoryChart.vue";
import RawMaterial from "./RawMaterial.vue";
import { BContainer, BRow, BCol, BSpinner, BCard, BButton, BAlert } from "bootstrap-vue";
import { mapActions, mapGetters } from "vuex";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";

export default {
  components: {
    BContainer,
    InventoryChart,
    BRow,
    RawMaterial,
    BCol,
    InventoryValueperProduct,
    BSpinner,
    ChartjsBarChart,
    BarChart,
    QuantityChart,
    Lead,
    BCard,
    BButton,
    BAlert,
    DatePicker,
  },

  data() {
    const today = new Date();
    return {
      dateRange: [today, today],
      inventoryValueAnalysisData: [],
      inventoryQtyAnalysisData: [],
      inventoryQtyProductSpecificData: [],
      warehouseDistributionData: [],
      isLocalLoading: false,
      firstProductId: null
    };
  },

  computed: {
    ...mapGetters("inventoryTransactionModule", {
      transactions: "transactions",
      loading: "loading",
    }),
    ...mapGetters("warehouseModule", {
      warehouses: "warehouse",
      warehouseLoading: "loading",
    }),

    ...mapGetters("productModule", {
      productList: "product",
      productLoading: "loading",
    }),

    ...mapGetters("rawmaterialModule", {
      materials: "rawmaterials",
      materialLoading: "loading",
    }),

    ...mapGetters("reportModule", {
      reportLoading: "loading",
    }),

    transactionCounts() {
      const counts = {};
      this.transactions.forEach((transaction) => {
        const type = transaction.type;
        counts[type] = (counts[type] || 0) + 1;
      });
      return counts;
    },
  },

  methods: {
    ...mapActions("inventoryTransactionModule", ["getTransactionListAction"]),
    ...mapActions("warehouseModule", ["getWarehouseListAction"]),
    ...mapActions("productModule", ["getProductListAction"]),
    ...mapActions("rawmaterialModule", ["getRawMaterialListAction"]),
    ...mapActions("reportModule", [
      "getInventoryValueAnalysisReportAction",
      "getInventoryQtyAnalysisReportAction",
      "getWarehouseDistributionReportAction"
    ]),

    formatDate(date) {
      return date.toISOString().split("T")[0];
    },

    async handleDateRangeChange() {
      if (!this.dateRange || !this.dateRange[0] || !this.dateRange[1]) {
        return;
      }

      const dateParams = {
        dateFrom: this.formatDate(this.dateRange[0]),
        dateTo: this.formatDate(this.dateRange[1]),
      };

      try {
        this.isLocalLoading = true;
        await this.fetchAllReports(dateParams);
      } catch (error) {
        console.error("Error updating reports:", error);
      } finally {
        this.isLocalLoading = false;
      }
    },

    async fetchAllReports(dateParams) {
      try {
        // Get products first to use in the second chart
        await this.getProductListAction().catch(err => console.error("Error fetching products:", err));
        
        // Set the first product ID if product list is loaded
        this.setFirstProductId();
        
        // Fetch all report data
        const [valueAnalysisResponse, warehouseDistributionResponse] = await Promise.all([
          this.getInventoryValueAnalysisReportAction(dateParams),
          // this.getInventoryQtyAnalysisReportAction(dateParams),
          this.getWarehouseDistributionReportAction(dateParams)
        ]);
        
        this.inventoryValueAnalysisData = valueAnalysisResponse || [];
        // this.inventoryQtyAnalysisData = qtyAnalysisResponse || [];
        this.warehouseDistributionData = warehouseDistributionResponse || [];

        // If we have a product ID, fetch product-specific data for the quantity chart
        if (this.firstProductId) {
          await this.fetchProductSpecificData(dateParams);
        }
      } catch (error) {
        console.error("Error fetching inventory reports:", error);
        throw error;
      }
    },
    
    setFirstProductId() {
      if (Array.isArray(this.productList) && this.productList.length > 0) {
        const firstProduct = this.productList.find(product => product && product.id);
        if (firstProduct) {
          this.firstProductId = firstProduct.id;
        }
      }
    },
    
    async fetchProductSpecificData(dateParams) {
      try {
        // Create a new params object with the product ID
        const productParams = {
          ...dateParams,
          itemId: this.firstProductId
        };
        
        // Make the API call for product-specific data
        const response = await this.getInventoryQtyAnalysisReportAction(productParams);
        this.inventoryQtyProductSpecificData = response || [];
        
        // If we got product-specific data, use it for the quantity chart
        if (this.inventoryQtyProductSpecificData.length > 0) {
          this.inventoryQtyAnalysisData = this.inventoryQtyProductSpecificData;
        }
      } catch (error) {
        console.error("Error fetching product-specific data:", error);
      }
    }
  },
  
  watch: {
    productList: {
      handler(newProducts) {
        if (Array.isArray(newProducts) && newProducts.length > 0 && !this.firstProductId) {
          this.setFirstProductId();
          
          // If we already have date range, fetch product-specific data
          if (this.dateRange && this.dateRange[0] && this.dateRange[1]) {
            const dateParams = {
              dateFrom: this.formatDate(this.dateRange[0]),
              dateTo: this.formatDate(this.dateRange[1]),
            };
            this.fetchProductSpecificData(dateParams);
          }
        }
      },
      immediate: true
    }
  },

  async mounted() {
    try {
      this.isLocalLoading = true;
      
      // Load initial reference data
      await Promise.all([
        this.getRawMaterialListAction().catch(err => console.error("Error fetching raw materials:", err)),
        this.getWarehouseListAction().catch(err => console.error("Error fetching warehouses:", err)),
        this.getTransactionListAction().catch(err => console.error("Error fetching transactions:", err))
      ]);
      
      // Load reports with date range
      await this.handleDateRangeChange();
    } catch (error) {
      console.error("Error during initial load:", error);
    } finally {
      this.isLocalLoading = false;
    }
  },
};
</script>